import { useEffect, useState } from "react";
import { Target, TrendingUp, AlertTriangle, Building2, ChevronRight } from "lucide-react";

const AnimatedNumber = ({ value, suffix = "", delay = 0 }: { value: number; suffix?: string; delay?: number }) => {
  const [current, setCurrent] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      const duration = 1500;
      const steps = 30;
      const increment = value / steps;
      let step = 0;
      
      const interval = setInterval(() => {
        step++;
        setCurrent(Math.min(Math.round(increment * step), value));
        if (step >= steps) clearInterval(interval);
      }, duration / steps);
      
      return () => clearInterval(interval);
    }, delay);
    
    return () => clearTimeout(timer);
  }, [value, delay]);
  
  return <span>{current}{suffix}</span>;
};

const AnimatedChart = () => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      const duration = 2000;
      const start = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - start;
        const p = Math.min(elapsed / duration, 1);
        setProgress(p);
        if (p < 1) requestAnimationFrame(animate);
      };
      
      requestAnimationFrame(animate);
    }, 800);
    
    return () => clearTimeout(timer);
  }, []);
  
  const points = [20, 35, 28, 45, 38, 55, 48, 65, 58, 72, 68, 78];
  const visiblePoints = Math.floor(progress * points.length);
  
  return (
    <div className="h-full flex items-end gap-1 px-2">
      {points.map((height, i) => (
        <div
          key={i}
          className="flex-1 bg-primary/20 rounded-t transition-all duration-300"
          style={{
            height: i < visiblePoints ? `${height}%` : '0%',
            opacity: i < visiblePoints ? 1 : 0,
          }}
        >
          <div 
            className="w-full bg-primary rounded-t transition-all duration-500"
            style={{ height: i < visiblePoints ? '100%' : '0%' }}
          />
        </div>
      ))}
    </div>
  );
};

const AnimatedHealthRing = () => {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => {
      const duration = 1500;
      const start = Date.now();
      
      const animate = () => {
        const elapsed = Date.now() - start;
        const p = Math.min(elapsed / duration, 1);
        setProgress(p * 77);
        if (p < 1) requestAnimationFrame(animate);
      };
      
      requestAnimationFrame(animate);
    }, 400);
    
    return () => clearTimeout(timer);
  }, []);
  
  const circumference = 2 * Math.PI * 40;
  const strokeDashoffset = circumference - (progress / 100) * circumference;
  
  return (
    <div className="relative w-24 h-24">
      <svg className="w-full h-full transform -rotate-90">
        <circle
          cx="48"
          cy="48"
          r="40"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          className="text-muted/30"
        />
        <circle
          cx="48"
          cy="48"
          r="40"
          stroke="currentColor"
          strokeWidth="8"
          fill="none"
          strokeLinecap="round"
          className="text-status-green"
          style={{
            strokeDasharray: circumference,
            strokeDashoffset,
            transition: 'stroke-dashoffset 0.1s ease-out',
          }}
        />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <span className="text-xl font-bold text-foreground">{Math.round(progress)}</span>
      </div>
    </div>
  );
};

const AnimatedKPITree = () => {
  const [expandedItems, setExpandedItems] = useState<number[]>([]);
  
  useEffect(() => {
    const items = [0, 1, 2, 3];
    items.forEach((item, index) => {
      setTimeout(() => {
        setExpandedItems(prev => [...prev, item]);
      }, 1200 + index * 300);
    });
  }, []);
  
  const treeItems = [
    { name: "TechCorp Inc.", status: "green", isParent: true },
    { name: "Sales", status: "green", isParent: false },
    { name: "Marketing", status: "yellow", isParent: false },
    { name: "Engineering", status: "green", isParent: false },
  ];
  
  return (
    <div className="space-y-1">
      {treeItems.map((item, index) => (
        <div
          key={index}
          className={`flex items-center gap-2 text-xs transition-all duration-300 ${
            expandedItems.includes(index) ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'
          }`}
          style={{ paddingLeft: item.isParent ? 0 : 16 }}
        >
          <ChevronRight className={`w-3 h-3 text-muted-foreground transition-transform ${item.isParent ? 'rotate-90' : ''}`} />
          <div className={`w-2 h-2 rounded-full ${
            item.status === 'green' ? 'bg-status-green' : 
            item.status === 'yellow' ? 'bg-status-yellow' : 'bg-status-red'
          }`} />
          <span className="text-foreground font-medium">{item.name}</span>
        </div>
      ))}
    </div>
  );
};

export function AnimatedDashboardPreview() {
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  
  useEffect(() => {
    setIsVisible(false);
    setIsFadingOut(false);
    
    // Fade in
    const showTimer = setTimeout(() => setIsVisible(true), 100);
    
    // Start fade out before reset (at 4.2s, giving 800ms for fade out)
    const fadeOutTimer = setTimeout(() => {
      setIsFadingOut(true);
    }, 4200);
    
    // Reset animation after fade out completes
    const replayTimer = setTimeout(() => {
      setAnimationKey(prev => prev + 1);
    }, 5000);
    
    return () => {
      clearTimeout(showTimer);
      clearTimeout(fadeOutTimer);
      clearTimeout(replayTimer);
    };
  }, [animationKey]);
  
  return (
    <div className="relative">
      <div 
        key={animationKey}
        className={`bg-card border border-border rounded-2xl shadow-2xl overflow-hidden transition-all duration-700 ease-in-out ${
          isFadingOut 
            ? 'opacity-0 scale-95' 
            : isVisible 
              ? 'opacity-100 scale-100' 
              : 'opacity-0 scale-95'
        }`}
      >
        {/* Window Header */}
        <div className="h-10 bg-muted flex items-center justify-between px-4 border-b border-border">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-status-red" />
            <div className="w-3 h-3 rounded-full bg-status-yellow" />
            <div className="w-3 h-3 rounded-full bg-status-green" />
          </div>
          <div className="flex items-center gap-2">
            <img src="/favicon.png" alt="Outsprinter" className="w-4 h-4" />
            <span className="text-xs font-semibold text-foreground">Outsprinter</span>
          </div>
          <div className="w-16" />
        </div>
        
        {/* Dashboard Content */}
        <div className="p-4 md:p-6 bg-background">
          {/* Header */}
          <div className="mb-4">
            <h3 className="text-sm md:text-base font-semibold text-foreground">Good morning, John!</h3>
            <p className="text-xs text-muted-foreground">Week 50, 2025 • TechCorp Inc.</p>
          </div>
          
          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-2 md:gap-3 mb-4">
            <div className="bg-card border border-border rounded-lg p-2 md:p-3">
              <div className="flex items-center gap-1 mb-1">
                <Target className="w-3 h-3 text-muted-foreground" />
                <span className="text-[10px] md:text-xs text-muted-foreground">Total KPIs</span>
              </div>
              <div className="text-lg md:text-xl font-bold text-foreground">
                <AnimatedNumber value={11} delay={200} key={animationKey} />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-2 md:p-3">
              <div className="flex items-center gap-1 mb-1">
                <TrendingUp className="w-3 h-3 text-status-green" />
                <span className="text-[10px] md:text-xs text-muted-foreground">On Track</span>
              </div>
              <div className="text-lg md:text-xl font-bold text-status-green">
                <AnimatedNumber value={64} suffix="%" delay={400} key={animationKey} />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-2 md:p-3">
              <div className="flex items-center gap-1 mb-1">
                <AlertTriangle className="w-3 h-3 text-status-yellow" />
                <span className="text-[10px] md:text-xs text-muted-foreground">At Risk</span>
              </div>
              <div className="text-lg md:text-xl font-bold text-status-yellow">
                <AnimatedNumber value={27} suffix="%" delay={600} key={animationKey} />
              </div>
            </div>
            
            <div className="bg-card border border-border rounded-lg p-2 md:p-3">
              <div className="flex items-center gap-1 mb-1">
                <Building2 className="w-3 h-3 text-muted-foreground" />
                <span className="text-[10px] md:text-xs text-muted-foreground">Depts</span>
              </div>
              <div className="text-lg md:text-xl font-bold text-foreground">
                <AnimatedNumber value={4} delay={800} key={animationKey} />
              </div>
            </div>
          </div>
          
          {/* Main Content */}
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            {/* Health Score */}
            <div className="bg-card border border-border rounded-lg p-3 flex flex-col items-center justify-center">
              <AnimatedHealthRing key={animationKey} />
              <span className="text-xs text-muted-foreground mt-2">Health Score</span>
            </div>
            
            {/* Chart */}
            <div className="col-span-2 bg-card border border-border rounded-lg p-3">
              <div className="text-xs font-medium text-foreground mb-2">Weekly Performance</div>
              <div className="h-20">
                <AnimatedChart key={animationKey} />
              </div>
            </div>
          </div>
          
          {/* KPI Tree */}
          <div className="mt-4 bg-card border border-border rounded-lg p-3">
            <div className="text-xs font-medium text-foreground mb-2">KPI Tree</div>
            <AnimatedKPITree key={animationKey} />
          </div>
        </div>
      </div>
    </div>
  );
}